import { PERCEPTION_MODEL_JOB, SUPERADMIN } from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  PerceptionModelJobHistoryQuery,
  type PerceptionModelJobRun,
} from '@graphql/queries/PerceptionModelQueries';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { CopyText, JobLauncher, JobRunTable } from 'components/JobLauncher';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import selectGgEnvironment from 'selectors/ggEnvironment';
import selectSessionToken from 'selectors/sessionToken';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import {
  argValue,
  defaultValues,
  trainPerceptionModels,
  type JobRunColumn,
} from 'services/jobSpecs';
import { hasSuperAdminRole } from 'utils/roleUtils';

import PerceptionModelError from './PerceptionModelError';
import { formatDay, shortVersion } from './PerceptionModelPicker';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * Start a perception model training run, and watch the ones already going.
 *
 * The script writes its own perception_model_job row, which takes minutes.
 * Until that row exists, the table shows a STARTED placeholder keyed on the
 * version id we minted, so a launch is visible the moment Batch accepts it.
 */

/** Runs still going are worth seeing without a reload; finished ones are not. */
const POLL_INTERVAL_MS = 30000;

/**
 * How long a submitted run gets to record itself before we stop expecting it.
 *
 * The row only exists once the container has started and run its insert, which
 * is minutes. But a job that fails before that — a bad definition, an image
 * pull error, no capacity — never writes one at all, and waiting forever is
 * both a permanent poll and a screen that never explains itself.
 */
const REGISTER_TIMEOUT_MS = 10 * 60 * 1000;

type PendingRun = {
  version: string;
  batchJobId: string;
  since: number;
  universe: string;
  pullDate: string;
};

/**
 * The row the table shows until the script records itself. STARTED is the
 * same status the job writes first, so the placeholder and the real row
 * read as one run appearing rather than two states.
 */
const optimisticRun = (awaited: PendingRun): PerceptionModelJobRun => ({
  id: -1,
  versionId: awaited.version,
  universe: awaited.universe,
  pullDate: awaited.pullDate,
  status: 'STARTED',
  statusReason: null,
  startedAt: new Date(awaited.since).toISOString(),
  finishedAt: null,
  isDeployed: false,
});

/**
 * Where a just-launched run has got to. Pure so the timeout is testable
 * without waiting ten minutes for it.
 */
const launchWaitState = (
  awaited: PendingRun | null,
  versions: string[],
  now: number,
): 'idle' | 'waiting' | 'registered' | 'lost' => {
  if (!awaited) return 'idle';
  if (versions.includes(awaited.version)) return 'registered';
  return now - awaited.since > REGISTER_TIMEOUT_MS ? 'lost' : 'waiting';
};

const RUNNING = ['STARTED', 'IN_PROGRESS', 'REQUESTED'];

const statusColor = (status: string) => {
  if (status === 'SUCCESS') return 'success' as const;
  if (status === 'FAILED') return 'error' as const;
  return 'default' as const;
};

/**
 * started_at is a timestamp, unlike pull_date — it has a real time of day, so
 * it is safe to hand to Date.
 */
const formatMoment = (value: string | null): string =>
  value ? new Date(value).toLocaleString() : '—';

const hoursBetween = (from: string, to: string | null): string => {
  if (!to) return '';
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  return `${(ms / 3600000).toFixed(1)}h`;
};

const TrainPerceptionModel: React.FC = () => {
  const token = useSelector((state) => selectSessionToken(state));
  const roles = useSelector((state) => selectTokenRoles(state));
  const allowed = hasSuperAdminRole(roles);
  // Read from the store rather than useGgEnvironment: the same value, already
  // fetched once at login, without giving this screen a second GraphQL
  // dependency just to decide which compute it may dispatch onto.
  const history = useHistory();
  const environment = useSelector((state) => selectGgEnvironment(state));

  const [awaited, setAwaited] = React.useState<PendingRun | null>(null);

  const { data, loading, error, startPolling, stopPolling } = useQuery(
    PerceptionModelJobHistoryQuery,
    { variables: { first: 25 } },
  );

  const runs: PerceptionModelJobRun[] =
    data?.allPerceptionModelJobs?.nodes ?? [];

  const inFlight = runs.filter((run) => RUNNING.includes(run.status));

  // Poll only while something can still change: a run in flight, or a launch
  // whose row has not appeared yet. A screen of finished runs re-querying every
  // thirty seconds for the rest of the day is pure noise. Each poll re-renders,
  // which is what eventually moves a stuck launch from waiting to lost.
  const waitState = launchWaitState(
    awaited,
    runs.map((run) => run.versionId),
    Date.now(),
  );
  const rows =
    waitState === 'waiting' && awaited
      ? [optimisticRun(awaited), ...runs]
      : runs;
  const shouldPoll = inFlight.length > 0 || waitState === 'waiting';

  React.useEffect(() => {
    if (shouldPoll) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
    return stopPolling;
  }, [shouldPoll, startPolling, stopPolling]);

  const columns: JobRunColumn<PerceptionModelJobRun>[] = [
    {
      key: 'status',
      label: 'Status',
      render: (run) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* The reason hangs off the status rather than the version: it is
              what someone reads a FAILED row to find out, and the version now
              carries its own copy tooltip. */}
          <Tooltip title={run.statusReason ?? ''} describeChild>
            <Chip
              size="small"
              label={run.status}
              color={statusColor(run.status)}
            />
          </Tooltip>
          {run.isDeployed && <Chip size="small" color="primary" label="live" />}
        </Box>
      ),
    },
    { key: 'universe', label: 'Universe' },
    {
      key: 'pullDate',
      label: 'Pull date',
      render: (run) => formatDay(run.pullDate),
    },
    {
      key: 'startedAt',
      label: 'Started',
      render: (run) => formatMoment(run.startedAt),
    },
    {
      key: 'finishedAt',
      label: 'Finished',
      render: (run) => {
        const elapsed = hoursBetween(run.startedAt, run.finishedAt);
        return (
          <>
            {formatMoment(run.finishedAt)}
            {elapsed && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 0.5, color: 'text.secondary' }}
              >
                ({elapsed})
              </Typography>
            )}
          </>
        );
      },
    },
    {
      key: 'versionId',
      label: 'Version',
      render: (run) => (
        // The failure reason is the whole value of a Failed row, and it is a
        // stack-shaped string, so it goes in a title rather than a column.
        <Typography variant="caption" title={run.statusReason ?? undefined}>
          {/* The date, not the uuid: the hash is forty characters that only
              matter when two runs share a day, and it crowded out every other
              column. It is still copied in full, and the model's own page
              prints it.

              Only linked once the row is real. The optimistic placeholder
              carries id -1 until the script records itself, and a link to
              model -1 is a page that can only say the model does not exist. */}
          {run.id > 0 ? (
            <Button
              size="small"
              title={run.versionId}
              onClick={() =>
                history.push(
                  PERCEPTION_MODEL_JOB.replace(':jobId', String(run.id)),
                )
              }
              sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
            >
              {shortVersion(run.versionId)}
            </Button>
          ) : (
            shortVersion(run.versionId)
          )}{' '}
          <CopyText value={run.versionId} title="Copy the full version id">
            copy
          </CopyText>
          {run.statusReason && ' ⚠'}
        </Typography>
      ),
    },
  ];

  return (
    <PagePaper>
      {/* Reached from Superadmin Tools, which is not in the sidebar, so the
          only way back is the browser button unless this is here. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(SUPERADMIN)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Superadmin Tools
      </Button>

      <PageHeader>Perception models</PageHeader>
      <PageTitle>{trainPerceptionModels.title}</PageTitle>

      <JobLauncher
        spec={trainPerceptionModels}
        initialValues={defaultValues()}
        environment={environment}
        token={token}
        allowed={allowed}
        onSubmitted={({ batchJobId, input }) => {
          // The version id is the only thread between the launch and the row
          // the script will write, so it is what the table waits on.
          const version = argValue(input, 'version_id');
          setAwaited(
            version
              ? {
                  version,
                  batchJobId,
                  since: Date.now(),
                  universe: argValue(input, 'universe') ?? '',
                  pullDate: argValue(input, 'pull_date') ?? '',
                }
              : null,
          );
        }}
      />

      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Run history
        </Typography>

        {waitState === 'lost' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            AWS Batch job {awaited.batchJobId} was accepted but never recorded a
            run, so it most likely failed before it could start — check it in
            the Batch console.
          </Alert>
        )}

        {error ? (
          <PerceptionModelError error={error} />
        ) : (
          <JobRunTable
            columns={columns}
            rows={rows}
            loading={loading}
            empty="No training runs recorded yet."
            highlightKey={(run) => run.versionId === awaited?.version}
          />
        )}
      </Box>
    </PagePaper>
  );
};

export default TrainPerceptionModel;
